import { sortBy } from "lodash-es";
import { type FC, useState } from "react";
import { type NoticeProps, components } from "react-select";

import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { t } from "i18n/i18n";
import { OptionType, OptionsType, TextListDropdown } from "mds/components/TextListDropdown";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { AddPlusIcon, TagIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";

interface AddOutcomeProps {
  hasReachedLimit?: boolean;
  options: OptionsType;
  limit?: number;
  iconOnly?: boolean;
  onSave: (ids: string[]) => void;
  isDisabled?: boolean;
  hideUnlessEditMode?: boolean;
  title?: string;
}

type NoOptionsMessageType = (hasReachedLimit: boolean) => FC<NoticeProps>;
const createNoOptionsMessage: NoOptionsMessageType = (hasReachedLimit) => {
  const NoOptionsMessageComponent: FC<NoticeProps> = (props) => (
    <components.NoOptionsMessage {...props}>
      <div className="text-black-tint-70">
        {t("add_outcome.no_options", {
          context: hasReachedLimit ? "full" : "empty",
        })}
      </div>
    </components.NoOptionsMessage>
  );

  NoOptionsMessageComponent.displayName = "NoOptionsMessage";

  return NoOptionsMessageComponent;
};

export const AddOutcome: FC<AddOutcomeProps> = ({
  hasReachedLimit,
  limit,
  onSave,
  options,
  iconOnly,
  hideUnlessEditMode,
  isDisabled,
  title,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<OptionsType>([]);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  if (hideUnlessEditMode && !isEditModeEnabled) {
    return null;
  }

  return (
    <CancelSaveMenu
      button={
        iconOnly ? (
          <SquareIconButton disabled={isDisabled} kind="tertiary" size="s" title={title}>
            <AddPlusIcon />
          </SquareIconButton>
        ) : (
          <HideUnlessEditModeButton isDisabled={isDisabled} reference="outcome" title={title} />
        )
      }
      saveEventName="Page Outcome - Add outcome"
      saveEventProps={{ outcomes: selectedOptions.map((option) => option.label) }}
      title={t("add_outcome.title")}
      onCancel={() => setSelectedOptions([])}
      onSave={() => {
        setSelectedOptions([]);
        onSave(selectedOptions.map((option) => option.value as string));
      }}
    >
      <TextListDropdown
        className="body-s w-full text-black-tint-20"
        components={{
          NoOptionsMessage: createNoOptionsMessage(hasReachedLimit ?? false),
        }}
        controlIcon={<TagIcon />}
        isOptionDisabled={(option: OptionType) =>
          limit === undefined
            ? false
            : selectedOptions.length >= limit &&
              !selectedOptions.map((o) => o.label).includes(option.label)
        }
        options={options}
        placeholder={t("add_outcome.search_placeholder")}
        value={sortBy(selectedOptions, (option) => option.label.toLowerCase())}
        useCheckboxOption
        onChange={(selected: OptionsType) => setSelectedOptions(selected)}
      />
    </CancelSaveMenu>
  );
};
